/**
 * Trial state hook + UI atoms used by the top nav and the "trial ended" gate.
 * Single fetch lives here so the shell doesn't do it twice.
 */
import { useEffect, useMemo, useState, createContext, useContext, useCallback } from "react";
import { Link } from "react-router-dom";
import { Clock, Sparkles, AlertTriangle, Info } from "lucide-react";
import { api } from "../lib/api";

const TrialCtx = createContext({
  loading: true,
  trialActive: false,
  daysRemaining: 0,
  subscribed: false,
  onFreeTier: false,
  hasAccess: true, // fail-open while loading so we don't lock a real user out
  refresh: () => {},
});

export function TrialProvider({ children }) {
  const [state, setState] = useState({
    loading: true,
    trialActive: false,
    daysRemaining: 0,
    subscribed: false,
    onFreeTier: false,
    hasAccess: true,
  });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/trial-status");
      setState({
        loading: false,
        trialActive: !!data.trial_active,
        daysRemaining: data.days_remaining || 0,
        subscribed: !!data.subscribed,
        onFreeTier: !!data.on_free_tier,
        hasAccess: !!data.has_access,
      });
    } catch (e) {
      // Fail open — a transient 5xx should NEVER kick a paying user out.
      console.debug("trial-status fetch failed", e?.response?.status || e?.message);
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => ({ ...state, refresh }), [state, refresh]);

  return (
    <TrialCtx.Provider value={value}>{children}</TrialCtx.Provider>
  );
}

export function useTrial() {
  return useContext(TrialCtx);
}

/**
 * TrialPill — small "27 days left in trial" badge in the top nav. Hidden if:
 * - Still loading (avoids UI flash)
 * - User has an active subscription (no need to nag)
 * - Trial has already ended (the gate takes over instead)
 * - Agent is sitting on the permanent Free tier (no countdown applies)
 */
export function TrialPill() {
  const { loading, trialActive, daysRemaining, subscribed, onFreeTier } = useTrial();
  if (loading || subscribed || onFreeTier || !trialActive) return null;

  const urgent = daysRemaining <= 3;
  // High-contrast: dark rose / dark emerald on tinted backgrounds so the
  // pill stays legible against the sticky header's white/blur.
  const colorCls = urgent
    ? "bg-rose-100 text-rose-900 ring-rose-300"
    : "bg-emerald-100 text-emerald-900 ring-emerald-300";

  return (
    <Link
      to="/settings#billing"
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset whitespace-nowrap hover:opacity-90 transition-opacity ${colorCls}`}
      data-testid="trial-pill"
      title={urgent
        ? "Trial ending soon — upgrade now to unlock DocuSign and unlimited clients"
        : "Free trial — upgrade any time to unlock DocuSign and unlimited clients"}
    >
      <Clock className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">{daysRemaining} {daysRemaining === 1 ? "day" : "days"} left</span>
      <span className="sm:hidden">{daysRemaining}d</span>
    </Link>
  );
}


/**
 * PlanStatusBanner — full-width ribbon under the app header.
 *
 * Two shapes:
 *   1. `onFreeTier` (permanent CRM plan) → neutral high-contrast INFO banner
 *      with the "Upgrade now to unlock DocuSign / more than 3 clients" CTA.
 *      Never times out — the Free tier has no expiration.
 *   2. Paid-plan trial in its last 7 days → the ORIGINAL amber/rose
 *      "trial ending" warning. Preserved for paying-tier trial flows.
 *
 * Order: onFreeTier check wins because a Free-tier agent's trial_end_at
 * is ignored (see backend `trial_status.on_free_tier`).
 */
export function TrialEndingBanner() {
  const { loading, trialActive, daysRemaining, subscribed, onFreeTier } = useTrial();
  if (loading || subscribed) return null;

  // ── 1. Free-tier (permanent) info banner ───────────────────────────
  if (onFreeTier) {
    return (
      <div
        className="w-full border-b border-slate-800 bg-slate-900 text-white"
        data-testid="free-tier-banner"
        role="status"
      >
        <div className="mx-auto max-w-7xl 2xl:max-w-[1600px] px-4 sm:px-6 py-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Info className="h-4 w-4 shrink-0 text-amber-300" aria-hidden="true" />
            <span className="text-sm font-semibold truncate">
              You are on the Free Tier (CRM only). Upgrade now to unlock DocuSign and add more than 3 clients.
            </span>
          </div>
          <Link
            to="/settings#billing"
            className="text-xs font-bold uppercase tracking-widest bg-amber-300 hover:bg-amber-200 text-slate-900 px-3 py-1.5 rounded-md whitespace-nowrap shrink-0"
            data-testid="free-tier-upgrade-btn"
          >
            Upgrade now →
          </Link>
        </div>
      </div>
    );
  }

  // ── 2. Legacy trial-ending warning (paid-tier trial flow) ──────────
  if (!trialActive || daysRemaining > 7) return null;

  const urgent = daysRemaining <= 3;
  const label = daysRemaining === 0
    ? "Your trial ends today"
    : daysRemaining === 1
      ? "Your trial ends tomorrow"
      : `Your trial ends in ${daysRemaining} days`;

  return (
    <div
      className={`w-full border-b ${urgent ? "bg-rose-600 border-rose-700 text-white" : "bg-amber-500 border-amber-600 text-white"}`}
      data-testid="trial-ending-banner"
      role="status"
    >
      <div className="mx-auto max-w-7xl 2xl:max-w-[1600px] px-4 sm:px-6 py-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="text-sm font-semibold truncate">
            {label} — Upgrade now to unlock DocuSign and unlimited clients.
          </span>
        </div>
        <Link
          to="/settings#billing"
          className="text-xs font-bold uppercase tracking-widest bg-white/95 hover:bg-white px-3 py-1.5 rounded-md whitespace-nowrap shrink-0"
          style={{ color: urgent ? "#9f1239" : "#92400e" }}
          data-testid="trial-ending-upgrade-btn"
        >
          Upgrade now →
        </Link>
      </div>
    </div>
  );
}


/**
 * TrialExpiredGate — full-screen paywall shown when:
 * - trial has ended AND
 * - agent has not subscribed
 *
 * Rendered by <Shell> INSTEAD OF the app content, so no route inside the
 * shell (leads, transactions, etc.) is reachable until the agent subscribes
 * or cancels. `/settings` stays reachable via the CTA so they can actually
 * pay.
 */
export function TrialExpiredGate() {
  const { refresh } = useTrial();
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16">
      <div className="max-w-lg w-full text-center rounded-2xl border border-slate-200 bg-white shadow-xl p-8 sm:p-10" data-testid="trial-expired-gate">
        <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center bg-amber-50 text-amber-600">
          <Sparkles className="h-7 w-7" />
        </div>
        <h1 className="mt-6 font-heading text-3xl font-bold tracking-tight text-slate-900">
          Your 30-day free trial has ended
        </h1>
        <p className="mt-4 text-slate-700 leading-relaxed">
          Your data — leads, transactions, client portals — is all safe.
          <b> Upgrade now to unlock DocuSign and unlimited clients.</b> No setup, no
          re-import, everything picks up where you left off.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/settings#billing"
            onClick={refresh}
            className="inline-flex items-center justify-center rounded-md px-6 py-3 text-sm font-semibold text-white shadow-sm hover:opacity-95 transition-opacity"
            style={{ background: "var(--primary-hex)" }}
            data-testid="trial-expired-subscribe-btn"
          >
            Subscribe to continue
          </Link>
          <button
            type="button"
            onClick={refresh}
            className="inline-flex items-center justify-center rounded-md px-6 py-3 text-sm font-medium text-slate-700 border border-slate-300 hover:bg-slate-50"
            data-testid="trial-expired-refresh-btn"
          >
            I just subscribed — refresh
          </button>
        </div>
        <p className="mt-6 text-xs text-slate-500">
          Questions? Email support@ikeycrm.com — we usually reply within a couple of hours.
        </p>
      </div>
    </div>
  );
}
